import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    CartLineModel,
    ProductSettingsModel,
    WarehouseDto,
    WarehouseModel,
} from "@insite/client-framework/Types/ApiModels";
import { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import ProductInventoryStockCount from "@WhiteCap/Components/ProductInventoryStockCount";
import setWarehouseStock from "@WhiteCap/Handlers/WarehouseHandler/StockWarehouse";
import FindLocationModal from "@WhiteCap/Overrides/Components/FindLocationModal";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    product?: ProductContextModel | CartLineModel;
    currentBranchStock: number;
    isAM: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    cart: getCurrentCartState(state).value,
    currentPage: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    updatePickUpWarehouse,
    setWarehouseStock,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    countAvailableGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    findLocationModal?: FindLocationModalStyles;
    wrapper?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 6,
        css: css`
            flex-grow: 0;
            > ${GridOffset} {
                display: grid;
            }
        `,
    },
    messageGridItem: { width: [12, 12, 6, 6, 6] },
    countAvailableGridItem: { width: [12, 12, 6, 6, 6] },
    availabilityByWarehouseLinkGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 16px !important;
                    `,
                    css`
                        margin-bottom: 16px !important;
                    `,
                    css``,
                    css``,
                    css`
                        margin-bottom: 16px !important;
                    `,
                ])}
        `,
    },
    availabilityByWarehouseLink: {
        css: css`
            font-size: 12px;
            font-family: inherit;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            font-size: 12px;
            line-height: 16px;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 5px;
    height: 25px;
    border-radius: 8px;
    border: 1px solid
        ${props => {
            const color = props.color;
            const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(color) || /^#([0-9A-F]{4}){1,2}$/i.test(color);
            if (isHex) {
                return color;
            }
            return safeColor(resolveColor(color, props.theme)).rgb().alpha(0.2).string();
        }};
    ${IconWrapper} {
        margin-right: 5px;
        height: 8px;
        width: 10px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "#DB8054";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "#54BCAB";
};

const getAvailabilityIconColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "#CA4200";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "#009B82";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    currentPage?: string,
    isProductDetailsPage?: boolean,
    productContext?: ProductContextModel,
): boolean => {
    if (productContext && productContext.product?.properties?.callForPrice === "true") {
        return false;
    }

    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    if (currentPage === "ProductListPage" || currentPage === "MyListsDetailsPage") {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    productContext,
    currentPage,
    product: pricingData,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    updatePickUpWarehouse,
    cart,
    setWarehouseStock,
    currentBranchStock,
    isAM,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [pid, setPid] = useState<string>("");
    const [modalKey, setModalKey] = useState(0);

    const modalCloseHandler = () => {
        setWarehouseStock({ isStockAvailable: true });
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        setModalKey(prev => prev + 1);
        setPid(productId);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconColor = getAvailabilityIconColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (!cart) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    const pricing = productContext?.productInfo?.pricing?.unitNetPrice ?? undefined;
    if (isAM && currentBranchStock > 0 && isProductDetailsPage) {
        inventoryTextComponent = <></>;
    } else if (
        availability?.messageType !== AvailabilityMessageType.NoMessage &&
        availability?.message?.length &&
        productContext.product?.properties?.callForPrice !== "true" &&
        pricing === undefined
    ) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon color={iconColor} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (
        availability?.messageType !== AvailabilityMessageType.NoMessage &&
        availability?.message?.length &&
        productContext.product?.properties?.callForPrice !== "true" &&
        pricing &&
        pricing > 0
    ) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon color={iconColor} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body:
                cart.fulfillmentMethod === FulfillmentMethod.PickUp
                    ? translate("Pick Up Address Updated")
                    : translate("Ship Address Updated"),
            messageType: "success",
        });
    };

    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        handlePickUpAddressChange(warehouse);
        setModalIsOpen(false);
    };
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {currentPage === "ProductDetailsPage" && (
                <GridItem {...styles.countAvailableGridItem}>
                    <ProductInventoryStockCount currentBranchStock={currentBranchStock} isAM={isAM} />
                </GridItem>
            )}
            {showLink(
                availability,
                trackInventory,
                productSettings,
                currentPage,
                isProductDetailsPage,
                productContext,
            ) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Branch")}
                    </Link>

                    <FindLocationModal
                        key={modalKey}
                        modalIsOpen={modalIsOpen}
                        onWarehouseSelected={handleWarehouseSelected}
                        onModalClose={modalCloseHandler}
                        extendedStyles={styles.findLocationModal}
                        inventoryWarehouses={warehouses}
                        isWarehouseInventoryLoading={isLoading}
                        warehouseInventoryErrorMessage={errorMessage}
                        pid={pid}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAvailability));
